/*
 * Description:
 * ---------------------
 * Dataset Collection panel for the Weavian Edge Console.
 *
 * Surfaces the edge-side ML training-data "flywheel": the per-flow records the
 * edge writes to its durable local archive (statistical flow features + the
 * nDPI-derived label). The live KPIs (GET /dataset/stats) confirm collection
 * is healthy and growing; the on-demand sampler (GET /dataset/sample) lets an
 * operator pull a small chunk of the most recently written records to verify
 * EXACTLY what is being persisted — without ever fetching the whole dataset.
 *
 * ! file components/DatasetPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useState } from "react";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanLabel } from "./WanNames";
import { MiniKPI } from "./DiagCharts";

const PROTO = { 1: "ICMP", 6: "TCP", 17: "UDP", 58: "ICMPv6" };
const protoName = (p) => PROTO[p] || (p != null ? String(p) : "—");

const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString([], { hour12: false }) +
    "." + String(d.getMilliseconds()).padStart(3, "0");
};

// One-decimal rendering for the routing metrics (QoE, latency, jitter, score).
const fmt1 = (v) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(1));
const fmtLoss = (v) => (v == null || isNaN(v) ? "—" : (Number(v) * 100).toFixed(2) + "%");
const fmtMbps = (v) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(3));

// Short, monospace-friendly rendering for the (possibly hashed) endpoints.
const Endpoint = ({ value }) => (
  <span className="dataset-mono" title={value}>{value || "—"}</span>
);

export default function DatasetPanel({ nodeApiUrl, selectedNode }) {
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(null);

  const [sample, setSample] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleErr, setSampleErr] = useState(null);
  const [sampleAt, setSampleAt] = useState(null);
  const [count, setCount] = useState(25);

  // Which durable archive to inspect: the per-flow classification dataset
  // ("flows") or the per-class routing decision→outcome dataset ("routing").
  const [stream, setStream] = useState("flows");
  const base = stream === "routing" ? "/dataset/routing" : "/dataset";

  // Switching streams invalidates the currently-shown sample.
  useEffect(() => { setSample(null); setSampleErr(null); setSampleAt(null); }, [stream]);

  // ── Lightweight live stats poll (cheap, confirms collection is healthy) ──
  useEffect(() => {
    if (!selectedNode) { setStats(null); return; }
    const controller = new AbortController();
    const poll = async () => {
      try {
        const url = nodeApiUrl(`${base}/stats`);
        if (!url) return;
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error("" + res.status);
        const data = await res.json();
        if (!controller.signal.aborted) { setStats(data); setStatsErr(null); }
      } catch (err) {
        if (err.name !== "AbortError") setStatsErr(err.message || "fetch failed");
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl, base]);

  // ── On-demand sample fetch (NOT polled — pulled only when requested) ──
  const loadSample = useCallback(async () => {
    if (!selectedNode) return;
    setSampleLoading(true);
    setSampleErr(null);
    try {
      const url = nodeApiUrl(`${base}/sample?n=${count}`);
      if (!url) throw new Error("no node selected");
      const res = await fetchWithTimeout(url, {}, 15000);
      if (!res.ok) throw new Error("" + res.status);
      const data = await res.json();
      setSample(data);
      setSampleAt(new Date());
    } catch (err) {
      setSampleErr(err.message || "fetch failed");
      setSample(null);
    } finally {
      setSampleLoading(false);
    }
  }, [selectedNode, nodeApiUrl, base, count]);

  if (!selectedNode) {
    return <div className="diag-empty">Select an edge node to inspect its collected dataset.</div>;
  }

  const enabled = stats?.enabled;
  const manifest = sample?.manifest;
  const records = Array.isArray(sample?.records) ? sample.records : [];

  return (
    <div className="dataset-panel">
      {/* ── Stream selector: flows (classification) vs routing (scheduling) ── */}
      <div className="dataset-toggle">
        <button className={stream === "flows" ? "active" : ""} onClick={() => setStream("flows")}>
          Flows
        </button>
        <button className={stream === "routing" ? "active" : ""} onClick={() => setStream("routing")}>
          Routing
        </button>
      </div>

      {/* ── Collector status ─────────────────────────────────────────── */}
      <div className="diag-card">
        <div className="diag-card-title">
          Collector Status
          <span className={"dataset-pill " + (enabled ? "ok" : "off")}>
            {statsErr ? "unreachable" : enabled ? "collecting" : "disabled"}
          </span>
        </div>

        {enabled ? (
          <>
            <div className="diag-kpi-strip">
              <MiniKPI label="Records Written" value={fmtNum(stats.written)} color="#22c55e" />
              <MiniKPI label="On Disk" value={fmtBytes(stats.bytes_on_disk)} color="#6366f1" />
              <MiniKPI label="Enqueued" value={fmtNum(stats.enqueued)} color="#06b6d4" />
              <MiniKPI label="Flushes" value={fmtNum(stats.flushes)} color="#8b5cf6" />
              <MiniKPI label="Dropped" value={fmtNum(stats.dropped)} color={stats.dropped > 0 ? "#ef4444" : "#64748b"} />
              <MiniKPI label="Pruned" value={fmtNum(stats.pruned)} color="#f59e0b" />
            </div>
            <div className="dataset-meta">
              <span><strong>Archive:</strong> <Endpoint value={stats.dir} /></span>
              <span><strong>Anonymized:</strong> {stats.anonymize ? "yes (salted SHA-256)" : "no (plaintext)"}</span>
            </div>
          </>
        ) : (
          <p className="dataset-note">
            {statsErr
              ? `Could not reach the collector telemetry endpoint (${statsErr}).`
              : stream === "routing"
                ? "Routing-record collection is disabled on this edge (NABX_ROUTESET_ENABLED=false)."
                : "Flow-record collection is disabled on this edge (NABX_DATASET_ENABLED=false)."}
          </p>
        )}
      </div>

      {/* ── On-demand sampler ────────────────────────────────────────── */}
      <div className="diag-card">
        <div className="diag-card-title">Verify Stored Data — Sample</div>
        <p className="dataset-note">
          Pull a small chunk of the <em>most recently written</em> records straight
          off the local archive to confirm what is actually being persisted. This
          is on-demand only — nothing is fetched until you ask for it.
        </p>

        <div className="dataset-controls">
          <label>
            Records:&nbsp;
            <select value={count} onChange={(e) => setCount(Number(e.target.value))}>
              {[10, 25, 50, 100, 250, 500].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <button className="dataset-btn" onClick={loadSample} disabled={sampleLoading}>
            {sampleLoading ? "Loading…" : "Fetch sample"}
          </button>
          {sampleAt && !sampleLoading && (
            <span className="dataset-fetched">fetched {ago(sampleAt)}</span>
          )}
          {sampleErr && <span className="dataset-err">{sampleErr}</span>}
        </div>

        {sample && (
          <div className="dataset-sample-summary">
            <span><strong>{sample.returned}</strong> record{sample.returned === 1 ? "" : "s"} returned</span>
            <span>·</span>
            <span><strong>{sample.partitions}</strong> hour-partition{sample.partitions === 1 ? "" : "s"} on disk</span>
            {sample.source && (<><span>·</span><span>newest: <Endpoint value={sample.source} /></span></>)}
          </div>
        )}

        {records.length > 0 && (
          <div className="dataset-table-wrap">
            {stream === "routing" ? (
              <table className="dataset-table">
                <thead>
                  <tr>
                    <th>Time (UTC)</th>
                    <th>Class</th>
                    <th>WAN</th>
                    <th>Path</th>
                    <th>Profile</th>
                    <th>QoE</th>
                    <th>Lat (ms)</th>
                    <th>Jit (ms)</th>
                    <th>Loss</th>
                    <th>Tput (Mbps)</th>
                    <th>Score</th>
                    <th>Packets</th>
                    <th>Bytes</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td className="dataset-mono">{fmtTs(r.ts)}</td>
                      <td><span className="dataset-class">{r.class}</span></td>
                      <td className="dataset-mono"><WanLabel iface={r.wan} /></td>
                      <td>{r.path_id}</td>
                      <td>{r.profile}</td>
                      <td>{fmt1(r.qoe)}</td>
                      <td>{fmt1(r.latency_ms)}</td>
                      <td>{fmt1(r.jitter_ms)}</td>
                      <td>{fmtLoss(r.loss_ratio)}</td>
                      <td>{fmtMbps(r.throughput_mbps)}</td>
                      <td>{fmt1(r.score)}</td>
                      <td>{fmtNum(r.packets)}</td>
                      <td>{fmtBytes(r.bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="dataset-table">
                <thead>
                  <tr>
                    <th>Time (UTC)</th>
                    <th>Event</th>
                    <th>Class</th>
                    <th>Proto</th>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Dport</th>
                    <th>Fwd (pkts/bytes)</th>
                    <th>Rev (pkts/bytes)</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td className="dataset-mono">{fmtTs(r.ts)}</td>
                      <td>
                        <span className={"dataset-evt " + (r.event === "open" ? "open" : "close")}>
                          {r.event}
                        </span>
                      </td>
                      <td><span className="dataset-class">{r.class}</span></td>
                      <td>{protoName(r.proto)}</td>
                      <td><Endpoint value={r.src} /><span className="dataset-port">:{r.sport}</span></td>
                      <td><Endpoint value={r.dst} /></td>
                      <td>{r.dport}</td>
                      <td>{fmtNum(r.fwd_pkts)} / {fmtBytes(r.fwd_bytes)}</td>
                      <td>{fmtNum(r.rev_pkts)} / {fmtBytes(r.rev_bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {sample && records.length === 0 && !sampleLoading && (
          <p className="dataset-note">
            {stream === "routing"
              ? "No routing records on disk yet. Records are written once per snapshot tick when a traffic class has carried packets on a WAN — generate some traffic and try again in a moment."
              : "No records on disk yet. Records are written when LAN-device flows are classified (open) and when they close — generate some traffic and try again."}
          </p>
        )}
      </div>

      {/* ── Dataset provenance / schema (from MANIFEST.json) ─────────── */}
      {manifest && (
        <div className="diag-card">
          <div className="diag-card-title">Dataset Manifest</div>
          <div className="dataset-meta">
            <span><strong>Node:</strong> {manifest.node_id || "—"}</span>
            <span><strong>Pilot:</strong> {manifest.pilot_id || "(untagged)"}</span>
            <span><strong>Agent:</strong> {manifest.agent_version || "—"}</span>
            <span><strong>Schema v:</strong> {manifest.record_version}</span>
            <span><strong>Anonymized:</strong> {manifest.anonymized ? "yes" : "no"}</span>
            {manifest.salt_fingerprint && (
              <span><strong>Salt fp:</strong> <span className="dataset-mono">{manifest.salt_fingerprint}</span></span>
            )}
          </div>
          {Array.isArray(manifest.fields) && (
            <div className="dataset-fields">
              <strong>Fields:</strong>{" "}
              {manifest.fields.map((f) => (
                <code key={f} className="dataset-field">{f}</code>
              ))}
            </div>
          )}
          {manifest.layout && (
            <div className="dataset-meta">
              <span><strong>Layout:</strong> <span className="dataset-mono">{manifest.layout}</span></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
